import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ReactFlowProvider } from 'reactflow';
import useStore from '../store/useStore';
import Toolbar from '../components/Toolbar';
import Canvas from '../components/Canvas';
import Sidebar from '../components/Sidebar';
import AuthModal from '../components/AuthModal';
import { useT } from '../i18n';

export default function GuestViewerPage() {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useStore((s) => s.theme);
  const accessToken = useStore((s) => s.accessToken);
  const restoreFromJSON = useStore((s) => s.restoreFromJSON);
  const resetEditor = useStore((s) => s.resetEditor);
  const setReadOnly = useStore((s) => s.setReadOnly);
  const selectedNodeId = useStore((s) => s.selectedNodeId);

  const [menuCollapsed, setMenuCollapsed] = useState(() => {
    return localStorage.getItem('editorSidebar') === 'closed';
  });
  const [showAuth, setShowAuth] = useState(false);

  const toggleMenu = () => {
    const next = !menuCollapsed;
    setMenuCollapsed(next);
    localStorage.setItem('editorSidebar', next ? 'closed' : 'open');
  };

  const template = location.state?.template;

  // If user is already logged in, hop straight to /editor with the template.
  useEffect(() => {
    if (accessToken && template) {
      navigate('/editor', { state: location.state, replace: true });
    }
  }, [accessToken, template, navigate, location.state]);

  // No template in state — go back to /templates.
  useEffect(() => {
    if (!template) {
      navigate('/templates', { replace: true });
    }
  }, [template, navigate]);

  // Load template into editor in read-only mode.
  useEffect(() => {
    if (!template) return;
    try {
      restoreFromJSON(template);
      setReadOnly(true);
    } catch {
      navigate('/templates');
    }
    return () => {
      setReadOnly(false);
      resetEditor();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAuthSuccess = () => {
    setShowAuth(false);
    setReadOnly(false);
    navigate('/editor', { state: location.state, replace: true });
  };

  if (!template) return null;

  return (
    <ReactFlowProvider>
      <div className={`app ${theme}`}>
        {showAuth && (
          <AuthModal onSuccess={handleAuthSuccess} onCancel={() => setShowAuth(false)} />
        )}

        <div className="main-layout">
          <Toolbar collapsed={menuCollapsed} onToggle={toggleMenu} />
          <div className="main-area">
            <Canvas />
            <Sidebar />
          </div>
        </div>

        <div className="guest-banner">
          <div className="guest-banner-text">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
              <circle cx="9" cy="9" r="7.5" />
              <path d="M9 5v4M9 12.5h.01" />
            </svg>
            <span>{t('guest.previewMode')}</span>
          </div>
          <button className="guest-banner-cta" onClick={() => setShowAuth(true)}>
            {t('guest.signInToEdit')} &rarr;
          </button>
        </div>

        <button
          className={`back-to-home ${selectedNodeId ? 'sidebar-open' : ''}`}
          onClick={() => navigate('/templates')}
          title={t('guest.backToTemplates')}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M12 8H4M4 8l4-4M4 8l4 4" />
          </svg>
        </button>
      </div>
    </ReactFlowProvider>
  );
}
